import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { canCreatePosting, getPostingLimit } from '@/lib/postingLimits'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import { HOCKEY_AGE_GROUPS, getAvailableTiers, REFEREE_TYPES } from '@/lib/hockeyData'
import PostingInsights from '@/components/scrimmage/PostingInsights'
import { toast } from 'sonner'

export default function ScrimmagePostingForm({ 
  team, 
  scrimmageManager, 
  existingPosting = null, 
  calendarEvent = null,
  activeCount = 0,
  onSuccess, 
  onCancel 
}) {
  const isPro = team?.season_tier === 'paid'
  const limit = getPostingLimit(team?.season_tier)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    posting_type: 'available',
    date: '',
    start_time: '',
    end_time: '',
    rink_name: '',
    rink_address: '',
    city: team?.city || '',
    state: team?.state || '',
    zip_code: team?.zip_code || '',
    team_level: team?.team_level || '',
    level_detail: team?.level_detail || '',
    prefer_non_district: false,
    referee_type: 'none',
    scrimmage_fee: '',
    home_and_home: false,
    notes: ''
  })

  useEffect(() => {
    // Pre-fill from existing posting
    if (existingPosting) {
      setFormData({
        posting_type: existingPosting.posting_type || 'available',
        date: existingPosting.date || '',
        start_time: existingPosting.start_time || '',
        end_time: existingPosting.end_time || '',
        rink_name: existingPosting.rink_name || '',
        rink_address: existingPosting.rink_address || '',
        city: existingPosting.city || '',
        state: existingPosting.state || '',
        zip_code: existingPosting.zip_code || '',
        team_level: existingPosting.team_level || '',
        level_detail: existingPosting.level_detail || '',
        prefer_non_district: existingPosting.prefer_non_district || false,
        referee_type: existingPosting.referee_type || 'none',
        scrimmage_fee: existingPosting.scrimmage_fee || '',
        home_and_home: existingPosting.home_and_home || false,
        notes: existingPosting.notes || ''
      })
    }
    // Pre-fill from calendar event
    else if (calendarEvent) {
      // Convert UTC time to local time for proper date/time display
      const eventDate = new Date(calendarEvent.start_time)
      
      // Get local date in YYYY-MM-DD format
      const year = eventDate.getFullYear()
      const month = String(eventDate.getMonth() + 1).padStart(2, '0')
      const day = String(eventDate.getDate()).padStart(2, '0')
      const localDate = `${year}-${month}-${day}`
      
      // Get local time in HH:MM format
      const hours = String(eventDate.getHours()).padStart(2, '0')
      const minutes = String(eventDate.getMinutes()).padStart(2, '0')
      const startTime = `${hours}:${minutes}`
      
      let endTime = ''
      if (calendarEvent.end_time) {
        const endDate = new Date(calendarEvent.end_time)
        const endHours = String(endDate.getHours()).padStart(2, '0')
        const endMinutes = String(endDate.getMinutes()).padStart(2, '0')
        endTime = `${endHours}:${endMinutes}`
      }

      // Parse location to extract rink name and address
      // Format examples: "VIC East Rink 8475 Kochia Ln Victoria MN 55386"
      //                  "CCC Rink 2 1661 Park Ridge Dr Chaska MN 55318"
      let rinkName = ''
      let rinkAddress = ''
      
      if (calendarEvent.location) {
        const location = calendarEvent.location.trim()
        // Try to split on first number (likely start of address)
        const addressMatch = location.match(/^([^0-9]+?)(\d.*)$/)
        if (addressMatch) {
          rinkName = addressMatch[1].trim()
          rinkAddress = addressMatch[2].trim()
        } else {
          // If no number found, use entire location as rink name
          rinkName = location
        }
      }

      setFormData(prev => ({
        ...prev,
        date: localDate,
        start_time: startTime,
        end_time: endTime,
        rink_name: rinkName,
        rink_address: rinkAddress,
        city: team?.city || '',
        state: team?.state || ''
      }))
    }
  }, [existingPosting, calendarEvent, team])

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      if (!scrimmageManager?.display_email && !scrimmageManager?.display_phone) {
        toast.error('Please enable either email or phone in Scrimmage Settings so teams can contact you.')
        setSaving(false)
        return
      }

      const postingData = {
        team_id: team.id,
        scrimmage_manager_id: scrimmageManager?.id || null,
        calendar_event_id: calendarEvent?.id || null,
        ...formData,
        scrimmage_fee: formData.scrimmage_fee ? parseFloat(formData.scrimmage_fee) : null,
        updated_at: new Date().toISOString()
      }

      if (existingPosting) {
        // Update existing posting
        const { error } = await supabase
          .from('scrimmage_postings')
          .update(postingData)
          .eq('id', existingPosting.id)

        if (error) throw error
      } else {
        // Create new posting - check limit first
        if (!canCreatePosting(activeCount, team?.season_tier)) {
          toast.error(`You have reached the limit of ${limit} active postings. Upgrade to Pro for unlimited postings!`)
          setSaving(false)
          return
        }
        
        const { data: newPosting, error } = await supabase
          .from('scrimmage_postings')
          .insert(postingData)
          .select('id')
          .single()

        if (error) throw error

        // Trigger immediate match generation for the new posting
        if (newPosting?.id) {
          try {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.access_token) {
              const matchResponse = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-matches-for-posting`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                  },
                  body: JSON.stringify({ posting_id: newPosting.id })
                }
              )
              
              if (matchResponse.ok) {
                const matchResult = await matchResponse.json()
                if (matchResult?.matchesGenerated > 0) {
                  toast.success(`Found ${matchResult.matchesGenerated} potential scrimmage partners!`)
                }
              }
            }
          } catch (matchErr) {
            // Silent fail - matches will be generated in nightly job
            console.warn('Immediate match generation failed:', matchErr)
          }
        }
      }

      if (onSuccess) onSuccess()
    } catch (err) {
      console.error('Error saving posting:', err)
      toast.error('Failed to save posting. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const availableTiers = getAvailableTiers(formData.team_level)

  return (
    <Card>
      <h2 className="font-heading text-xl font-bold mb-4">
        {existingPosting ? 'Edit Scrimmage Posting' : calendarEvent ? 'Post From Calendar Event' : 'New Scrimmage Posting'}
      </h2>

      {!team?.name && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            We don’t have a team name on file. Update your Scrimmage Settings so opponents know who you are.
          </p>
        </div>
      )}

      {calendarEvent && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-800">
            📅 <strong>Imported from calendar:</strong> {calendarEvent.title}
            {calendarEvent.description && ` - ${calendarEvent.description}`}
          </p>
        </div>
      )}

      {!isPro && !existingPosting && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            Free tier: {activeCount}/{limit} active postings used. 
            <Link to="/pricing" className="underline ml-1">Upgrade to Pro</Link> for unlimited.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Posting Type */}
        <div>
          <label className="label">Posting Type *</label>
          <div className="mt-2 space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="available"
                checked={formData.posting_type === 'available'}
                onChange={(e) => handleChange('posting_type', e.target.value)}
                className="mr-2 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                required
              />
              <span className="text-brand-charcoal">
                <strong>Available</strong> - I have home ice available for a scrimmage
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="wanted"
                checked={formData.posting_type === 'wanted'}
                onChange={(e) => handleChange('posting_type', e.target.value)}
                className="mr-2 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                required
              />
              <span className="text-brand-charcoal">
                <strong>Wanted</strong> - I'm looking for an away scrimmage
              </span>
            </label>
          </div>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Date *"
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            required
          />
          <Input
            label="Start Time *"
            type="time"
            value={formData.start_time}
            onChange={(e) => handleChange('start_time', e.target.value)}
            required
          />
          <Input
            label="End Time"
            type="time"
            value={formData.end_time}
            onChange={(e) => handleChange('end_time', e.target.value)}
          />
        </div>

        {/* Location (for available postings) */}
        {formData.posting_type === 'available' && (
          <div>
            <h3 className="font-medium text-brand-charcoal mb-3">Ice Location</h3>
            <div className="space-y-4">
              <Input
                label="Rink Name"
                value={formData.rink_name}
                onChange={(e) => handleChange('rink_name', e.target.value)}
                placeholder="e.g., Victoria Community Center"
              />
              <Input
                label="Rink Address"
                value={formData.rink_address}
                onChange={(e) => handleChange('rink_address', e.target.value)}
                placeholder="e.g., 1305 St Albans St"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="City *"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Victoria"
                  required
                />
                <Input
                  label="State *"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., MN"
                  maxLength={2}
                  required
                />
                <Input
                  label="Zip Code"
                  value={formData.zip_code}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  placeholder="e.g., 55386"
                />
              </div>
            </div>
          </div>
        )}

        {/* Team Level */}
        <div>
          <h3 className="font-medium text-brand-charcoal mb-3">Team Level</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Age Group *"
              value={formData.team_level}
              onChange={(e) => {
                handleChange('team_level', e.target.value)
                handleChange('level_detail', '') // Reset tier
              }}
              options={[
                { value: '', label: 'Select age group' },
                ...HOCKEY_AGE_GROUPS
              ]}
              required
            />
            
            {availableTiers.length > 0 && (
              <Select
                label="Team Tier (Optional)"
                value={formData.level_detail}
                onChange={(e) => handleChange('level_detail', e.target.value)}
                options={[
                  { value: '', label: 'Any tier' },
                  ...availableTiers
                ]}
              />
            )}
          </div>
        </div>

        {/* Preferences */}
        <div>
          <h3 className="font-medium text-brand-charcoal mb-3">Preferences</h3>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.prefer_non_district}
                onChange={(e) => handleChange('prefer_non_district', e.target.checked)}
                className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
              />
              <span className="text-sm text-brand-charcoal">Prefer non-district opponents</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Referee Type"
                value={formData.referee_type}
                onChange={(e) => handleChange('referee_type', e.target.value)}
                options={REFEREE_TYPES}
              />

              <Input
                label="Scrimmage Fee (Optional)"
                type="number"
                step="0.01"
                min="0"
                value={formData.scrimmage_fee}
                onChange={(e) => handleChange('scrimmage_fee', e.target.value)}
                placeholder="e.g., 75.00"
                disabled={formData.referee_type !== 'paid_refs'}
              />
            </div>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.home_and_home}
                onChange={(e) => handleChange('home_and_home', e.target.checked)}
                className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
              />
              <span className="text-sm text-brand-charcoal">
                Looking for home-and-home (reciprocal scrimmage)
              </span>
            </label>
          </div>
        </div>

        {/* Demand Insights */}
        {formData.date && formData.team_level && (
          <PostingInsights
            date={formData.date}
            level={formData.team_level}
            levelDetail={formData.level_detail}
            postingType={formData.posting_type}
          />
        )}

        {/* Notes */}
        <Textarea
          label="Additional Notes (Optional)"
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Any additional details about the scrimmage..."
          rows={3}
        />

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : existingPosting ? 'Update Posting' : 'Post Scrimmage'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

